import { Link, NavLink, useNavigate } from "react-router-dom";
import { Flame, Zap, LogOut, Route, MessageCircle, LayoutGrid, Languages, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OfflineBadge, InstallButton } from "@/components/OfflineBadge";

const navCls = ({ isActive }) =>
  `flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
    isActive ? "bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))]" : "text-slate-600 hover:bg-slate-100"
  }`;

export const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lang = user?.current_language;
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/80">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
        <Link to="/dashboard" data-testid="brand-link" className="flex items-center gap-2 mr-2">
          <span className="h-9 w-9 rounded-xl bg-[hsl(var(--primary))] text-white grid place-items-center font-display font-extrabold">L</span>
          <span className="font-display font-extrabold text-lg text-slate-900 hidden sm:inline">LinguaPath</span>
        </Link>
        <nav className="flex items-center gap-1 overflow-x-auto">
          <NavLink to="/dashboard" className={navCls} data-testid="nav-dashboard"><LayoutGrid size={16} /><span className="hidden md:inline">Dashboard</span></NavLink>
          <NavLink to={lang ? `/learn/${lang}` : "/languages"} className={navCls} data-testid="nav-path"><Route size={16} /><span className="hidden md:inline">Path</span></NavLink>
          <NavLink to={lang ? `/tutor/${lang}` : "/languages"} className={navCls} data-testid="nav-tutor"><MessageCircle size={16} /><span className="hidden md:inline">AI Tutor</span></NavLink>
          <NavLink to={lang ? `/review/${lang}` : "/languages"} className={navCls} data-testid="nav-review"><RotateCcw size={16} /><span className="hidden md:inline">Review</span></NavLink>
          <NavLink to="/languages" end className={navCls} data-testid="nav-languages"><Languages size={16} /><span className="hidden md:inline">Languages</span></NavLink>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <InstallButton />
          <OfflineBadge />
          <span data-testid="header-streak" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[hsl(var(--secondary-light))] text-[hsl(var(--secondary-dark))] text-sm font-bold">
            <Flame size={16} className={user?.active_today ? "fill-current" : ""} />{user?.streak ?? 0}
          </span>
          <span data-testid="header-xp" className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
            <Zap size={16} className="text-amber-500 fill-amber-400" />{user?.xp ?? 0} XP
          </span>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="h-9 w-9 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
            aria-label="Log out"
          >
            <LogOut size={17} />
          </button>
        </div>
      </div>
    </header>
  );
};
